<template>
	<div
		ref="multiFormatPlayer"
		class="multi-format-player"
		:class="[`is-${uiStatus}`, { 'is-mobile-portrait': internalIsMobilePortrait, 'has-active-keyframe': hasActiveKeyframe }]"
	>
		<div class="multi-format-player__media-w js-multi-format-tool-player-media">
			<div class="multi-format-player__media-w-inner">
				<div
					ref="videoContainer"
					class="multi-format-player__video-container"
				>
					<slot name="video-overlay" />
					<video
						ref="videoPlayer"
						class="multi-format-player__video-player"
						playsinline
						preload="metadata"
					/>
				</div>
				<div
					v-show="uiStatus !== 'preview'"
					ref="cropContainer"
					class="multi-format-player__crop-container"
					:class="{ 'has-crop-graphic': !!graphicImageUrl }"
					:style="cropContainerStyle"
				>
					<canvas
						ref="cropCanvas"
						class="multi-format-player__crop-canvas"
					/>
				</div>
			</div>
		</div>
		<slot
			v-if="hasControlsReplacement"
			name="controls"
		/>
		<template v-else>
			<PlayerControls
				v-show="timeUpdateReceived"
				ref="playerControls"
				class="multi-format-player__controls"
				:class="{ 'is-hidden': !showControls && !showRecordingControls, 'is-inactive': showRecordingControls }"
				:inert="!showControls ? '' : null"
				:duration="showRecordingControls ? 0 : displayPlayerControlsDuration"
				:position="showRecordingControls ? 0 : displayPlayerControlsPosition"
				:is-playing="showRecordingControls ? false : isPlaying"
				:show-timeline="showPlayerControlsTimeline"
				:show-scrubber="showPlayerControlsTimeline"
				:is-muted="isMuted"
				:volume-level="volumeLevel"
				:audio-tracks="audioTracks"
				:current-audio-track-id="currentAudioTrackId"
				:show-fullscreen="false"
				:show-share-button="false"
				:timeline-full-width="true"
				:controls-full-width="!isMobileViewport"
				:show-volume-selector="!internalIsMobilePortrait"
				:dark-theme="$arenaConfig?.isLightTheme && isCommunityCropping"
				@play="togglePlay(true)"
				@pause="togglePlay(false)"
				@set-mute="setMute($event)"
				@set-volume="setVolume($event)"
				@set-audio-track="setAudioTrack($event)"
				@seek="setVideoTime($event)"
			/>
		</template>
	</div>
</template>

<script>
import Hls from 'hls.js';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import PlayerControls from '@shared/components/PlayerControls/PlayerControls.vue';
import { getAudiotrackLabel } from '@shared/utils/audiotrack';
import { GraphicAspectRatio } from '@shared/enums/GraphicAspectRatio.js';
import { getGraphicAspectRatioValue } from '@shared/utils/graphicAspectRatio.js';

const CROPPER_SETTINGS = {
	background:       false,
	modal:            false,
	center:           false,
	guides:           false,
	cropBoxResizable: false,
	aspectRatio:      9 / 16,
	autoCropArea:     1,
	viewMode:         0,
	dragMode:         'move',
	responsive:       true,
	zoomable:         false,
	scalable:         false,
};

const urlParams = new URLSearchParams(window.location.search);
const requestedPosition = urlParams.get('position');
const requestedAudioTrackId = Number.parseInt(urlParams.get('audioTrackId'), 10);

export default {
	name:       'MultiFormatPlayer',
	components: {
		PlayerControls,
	},
	props: {
		hlsUrl: {
			type:    String,
			default: null,
		},
		uiStatus: {
			type:     String,
			required: true,
		},
		showControls: {
			type:    Boolean,
			default: true,
		},
		tagTime: {
			type:    Object,
			default: null,
		},
		internalIsMobilePortrait: {
			type:    Boolean,
			default: false,
		},
		isMobileViewport: {
			type:    Boolean,
			default: false,
		},
		streamLeadTimeSeconds: {
			type:    Number,
			default: 0,
		},
		forcedDuration: {
			type:    Number,
			default: undefined,
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
		hasActiveKeyframe: {
			type:    Boolean,
			default: false,
		},
		hasControlsReplacement: {
			type:    Boolean,
			default: false,
		},
		playerControlsDurationOverride: {
			type:    Number,
			default: null,
		},
		playerControlsPositionOverride: {
			type:    Number,
			default: null,
		},
		showPlayerControlsTimeline: {
			type:    Boolean,
			default: true,
		},
		aspectRatio: {
			type:    String,
			default: GraphicAspectRatio.AR_9_16,
		},
		graphicImageUrl: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			duration:                      0,
			position:                      requestedPosition ? parseFloat(requestedPosition) : 0,
			isPlaying:                     false,
			isMuted:                       false,
			volumeLevel:                   50,
			containerWidth:                1200,
			cropper:                       null,
			timeUpdateReceived:            false,
			hls:                           null,
			audioTracks:                   [],
			currentAudioTrackId:           null,
			currentAudioTrack:             null,
			hasAppliedRequestedAudioTrack: false,
			mediaResizeObserver:           null,
			mediaResizeFrame:              null,
			controlsLayoutResizeFrame:     null,
		};
	},
	computed: {
		// Keep the familiar controls visible without suggesting they can change a recording.
		showRecordingControls() {
			return ['ready', 'recording'].includes(this.uiStatus);
		},
		playerControlsDuration() {
			return this.tagTime ? this.tagTime.end - this.tagTime.start : this.duration - this.streamLeadTimeSeconds;
		},
		playerControlsPosition() {
			return this.tagTime ? this.position - this.tagTime.start : this.position - this.streamLeadTimeSeconds;
		},
		displayPlayerControlsDuration() {
			return this.playerControlsDurationOverride ?? this.playerControlsDuration;
		},
		displayPlayerControlsPosition() {
			return this.playerControlsPositionOverride ?? this.playerControlsPosition;
		},
		cropContainerStyle() {
			return {
				'--multi-format-player-crop-graphic': this.graphicImageUrl
					? `url("${this.$Helpers.getScaledImageUrl(this.graphicImageUrl, 1080)}")`
					: 'none',
			};
		},
	},
	watch: {
		playerControlsDuration() {
			this.$emit('duration-update', this.playerControlsDuration);
		},
		timeUpdateReceived(value, oldValue) {
			if (value && !oldValue) {
				this.resizeCropperAfterControlsLayout();
			}
		},
		showPlayerControlsTimeline() {
			this.resizeCropperAfterControlsLayout();
		},
		aspectRatio() {
			this.updateCropperAspectRatio();
		},
	},
	mounted() {
		if (this.hlsUrl && Hls.isSupported()) {
			this.hls = new Hls();
			this.hls.loadSource(this.hlsUrl);
			this.hls.attachMedia(this.$refs.videoPlayer);
			this.registerHlsAudioTrackListeners();
		} else if (this.$refs.videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
			this.$refs.videoPlayer.src = this.hlsUrl; // safari fix
			this.registerNativeAudioTrackListeners();
		}

		this.$refs.videoPlayer.addEventListener('loadedmetadata', this.handleLoadedMetadata);
		this.$refs.videoPlayer.addEventListener('durationchange', this.updateDuration);
		this.$refs.videoPlayer.addEventListener('timeupdate', this.handleTimeUpdate);
		this.$refs.videoPlayer.addEventListener('progress', this.handleProgress);

		this.$refs.videoPlayer.removeAttribute('controls');

		this.initializeCropper();
		this.observeMediaResize();

		this.$refs.cropCanvas.addEventListener('ready', this.handleCropperReady);
		this.$refs.cropCanvas.addEventListener('cropstart', this.handleCropStart);
	},
	beforeDestroy() {
		// Clean up event listeners to prevent memory leaks
		this.$refs.videoPlayer.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
		this.$refs.videoPlayer.removeEventListener('durationchange', this.updateDuration);
		this.$refs.videoPlayer.removeEventListener('timeupdate', this.handleTimeUpdate);
		this.$refs.videoPlayer.removeEventListener('progress', this.handleProgress);
		this.$refs.videoPlayer?.audioTracks?.removeEventListener('change', this.updateNativeAudioTrackInformation);
		this.$refs.cropCanvas.removeEventListener('ready', this.handleCropperReady);
		this.$refs.cropCanvas.removeEventListener('cropstart', this.handleCropStart);
		this.stopObservingMediaResize();

		// Destroy cropper instance
		if (this.cropper) {
			this.cropper.destroy();
			this.cropper = null;
		}
		if (this.hls) {
			this.hls.destroy();
			this.hls = null;
		}
	},
	methods: {
		registerNativeAudioTrackListeners() {
			const nativeAudioTracks = this.$refs.videoPlayer?.audioTracks;
			if (!nativeAudioTracks) {
				return;
			}

			nativeAudioTracks.addEventListener('change', this.updateNativeAudioTrackInformation);
		},
		registerHlsAudioTrackListeners() {
			if (!this.hls) {
				return;
			}

			this.hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, (_event, data) => {
				this.audioTracks = data?.audioTracks?.map((track, index) => ({
					id:       track.id ?? index,
					label:    getAudiotrackLabel(track, index),
					language: track?.language || track?.lang || null,
					selected: track.default || false,
				})) || [];

				const selectedAudioTrack = this.audioTracks.find((track) => track.selected);
				this.currentAudioTrackId = selectedAudioTrack?.id ?? 0;
				this.syncCurrentAudioTrack();
				this.applyRequestedAudioTrack();
			});

			this.hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, (_event, data) => {
				this.currentAudioTrackId = data?.id ?? 0;
				this.audioTracks = this.audioTracks.map((track) => ({
					...track,
					selected: track.id === data?.id,
				}));
				this.syncCurrentAudioTrack();
			});
		},
		updateNativeAudioTrackInformation() {
			const nativeAudioTracks = this.$refs.videoPlayer?.audioTracks;
			if (!nativeAudioTracks) {
				return;
			}

			this.audioTracks = Array.from(nativeAudioTracks).map((track, index) => ({
				id:       index,
				label:    getAudiotrackLabel(track, index),
				language: track?.language || track?.lang || null,
				selected: track.enabled,
			}));

			const selectedAudioTrack = this.audioTracks.find((track) => track.selected);
			this.currentAudioTrackId = selectedAudioTrack?.id ?? 0;
			this.syncCurrentAudioTrack();
			this.applyRequestedAudioTrack();
		},
		syncCurrentAudioTrack() {
			this.currentAudioTrack = this.audioTracks.find((track) => track.id === this.currentAudioTrackId) ?? null;
			this.$emit('audio-track-change', this.currentAudioTrack);
		},
		applyRequestedAudioTrack() {
			if (this.hasAppliedRequestedAudioTrack || !Number.isFinite(requestedAudioTrackId)) {
				return;
			}

			if (!this.audioTracks.some((track) => Number.parseInt(track.id, 10) === requestedAudioTrackId)) {
				return;
			}

			this.hasAppliedRequestedAudioTrack = true;
			this.setAudioTrack(requestedAudioTrackId);
		},
		setAudioTrack(id) {
			const numericId = Number.parseInt(id, 10);
			if (!Number.isFinite(numericId)) {
				return;
			}

			if (this.hls) {
				if (this.hls.audioTrack !== numericId) {
					this.hls.audioTrackController.setAudioTrack(numericId);
				}
				return;
			}

			const nativeAudioTracks = this.$refs.videoPlayer?.audioTracks;
			if (!nativeAudioTracks?.length) {
				return;
			}

			for (let index = 0; index < nativeAudioTracks.length; index++) {
				nativeAudioTracks[index].enabled = index === numericId;
			}

			this.updateNativeAudioTrackInformation();
		},
		initializeCropper() {
			this.cropper = new Cropper(this.$refs.cropCanvas, {
				...CROPPER_SETTINGS,
				aspectRatio: getGraphicAspectRatioValue(this.aspectRatio),
				cropmove:    this.handleCropMove,
			});
		},
		updateCropperAspectRatio() {
			if (!this.cropper) {
				return;
			}

			this.cropper.setAspectRatio(getGraphicAspectRatioValue(this.aspectRatio));
			this.$nextTick(() => {
				this.setCropBoxToFullHeight();
				this.syncCropBoxData();
			});
		},
		setCropBoxToFullHeight() {
			if (!this.isCropperConnected()) {
				return;
			}

			const cropContainerSize = this.getCropContainerSize();
			if (cropContainerSize.width <= 0 || cropContainerSize.height <= 0) {
				return;
			}

			const cropBoxData = this.cropper.getCropBoxData();
			const cropBoxWidth = Math.min(cropContainerSize.width, cropContainerSize.height * getGraphicAspectRatioValue(this.aspectRatio));
			const maxLeft = cropContainerSize.width - cropBoxWidth;
			const left = cropBoxData.width > 0
				? Math.max(0, Math.min(cropBoxData.left, maxLeft))
				: maxLeft / 2;

			this.cropper.setCropBoxData({
				left,
				top:    0,
				width:  cropBoxWidth,
				height: cropContainerSize.height,
			});
		},
		observeMediaResize() {
			if (!window.ResizeObserver || !this.$refs.videoContainer) {
				return;
			}

			this.mediaResizeObserver = new ResizeObserver(() => {
				this.scheduleMediaResize();
			});
			this.mediaResizeObserver.observe(this.$refs.videoContainer);
		},
		stopObservingMediaResize() {
			cancelAnimationFrame(this.mediaResizeFrame);
			cancelAnimationFrame(this.controlsLayoutResizeFrame);
			this.mediaResizeFrame = null;
			this.controlsLayoutResizeFrame = null;
			if (this.mediaResizeObserver) {
				this.mediaResizeObserver.disconnect();
				this.mediaResizeObserver = null;
			}
		},
		scheduleMediaResize() {
			if (!this.$refs.videoContainer?.isConnected || !this.$refs.cropContainer?.isConnected) {
				return;
			}

			cancelAnimationFrame(this.mediaResizeFrame);
			this.mediaResizeFrame = requestAnimationFrame(this.handleMediaResize);
		},
		resizeCropperAfterControlsLayout() {
			this.$nextTick(() => {
				this.$emit('controls-layout-change');
				this.scheduleMediaResize();
				cancelAnimationFrame(this.controlsLayoutResizeFrame);
				this.controlsLayoutResizeFrame = requestAnimationFrame(() => {
					this.$emit('controls-layout-change');
					this.scheduleMediaResize();
				});
			});
		},
		handleMediaResize() {
			if (!this.$refs.videoContainer?.isConnected) {
				return;
			}

			// Preview still needs current media dimensions while the cropper is hidden.
			const { width, height } = this.$refs.videoContainer.getBoundingClientRect();
			this.$emit('media-size-change', { width, height });
			if (!this.isCropperReadyForResize() || this.uiStatus === 'preview') {
				return;
			}

			if (typeof this.cropper.resize === 'function') {
				this.cropper.resize();
			} else if (typeof this.cropper.render === 'function') {
				this.cropper.render();
			}

			this.setCropBoxToFullHeight();
			this.syncCropBoxData();
		},
		handleCropMove() {
			this.setCropBoxToFullHeight();
			this.syncCropBoxData();
		},
		async handleTimeUpdate(event) {
			// Safari can expose the seekable range before it emits a useful progress event.
			this.updateDuration();
			this.position = event.target?.currentTime;
			this.timeUpdateReceived = true;
			this.$emit('position-update', this.position);

			// stopping 0.1s before the end to prevent some glitches
			const hasValidDuration = Number.isFinite(this.duration) && this.duration > 0;
			if ((this.tagTime && this.position + 0.1 >= this.tagTime.end) || (hasValidDuration && this.position + 0.1 >= this.duration)) {
				await this.togglePlay(false);
				this.$emit('video-ended');
			}
		},
		handleProgress() {
			this.updateDuration();
		},
		// Native Safari HLS can report a finite but stale media duration, so prefer the
		// furthest valid boundary instead of only falling back to the seekable range.
		updateDuration() {
			const videoPlayer = this.$refs.videoPlayer;
			const durationCandidates = [videoPlayer?.duration];

			if (videoPlayer?.seekable?.length > 0) {
				durationCandidates.push(videoPlayer.seekable.end(videoPlayer.seekable.length - 1));
			}

			const rawDuration = durationCandidates
				.filter(duration => Number.isFinite(duration) && duration >= 0)
				.reduce((highestDuration, duration) => Math.max(highestDuration, duration), 0);

			const maxDuration = Number.isFinite(this.forcedDuration)
				? this.forcedDuration + this.streamLeadTimeSeconds
				: rawDuration;
			const duration = Math.min(rawDuration, maxDuration);

			this.duration = Math.max(0, duration);
		},
		async togglePlay(value) {
			if (value) {
				await this.$refs.videoPlayer.play();
			} else {
				this.$refs.videoPlayer.pause();
			}
			this.isPlaying = value;
			this.$emit('play-state-change', this.isPlaying);
		},
		pause() {
			this.$refs.videoPlayer.pause();
			this.isPlaying = false;
			this.$emit('play-state-change', this.isPlaying);
		},
		async play() {
			await this.$refs.videoPlayer.play();
			this.isPlaying = true;
			this.$emit('play-state-change', this.isPlaying);
		},
		async setVideoTime(time, streamLeadTimeIncluded = false) {
			if (!streamLeadTimeIncluded) {
				time += this.streamLeadTimeSeconds;
			}
			if (this.tagTime) {
				time += this.tagTime.start;
			}
			this.position = time;
			this.$refs.videoPlayer.currentTime = time;
		},
		seek(time) {
			if (this.displayPlayerControlsPosition + time < 0) {
				this.setVideoTime(0);
			} else if (this.displayPlayerControlsPosition + time > this.displayPlayerControlsDuration) {
				this.setVideoTime(this.displayPlayerControlsDuration);
			} else {
				this.setVideoTime(this.displayPlayerControlsPosition + time);
			}
		},
		getPlayerData() {
			this.isPlaying = !this.$refs.videoPlayer.paused;
			this.isMuted = this.$refs.videoPlayer.muted;
			this.volumeLevel = this.$refs.videoPlayer.volume * 100;
		},
		getCurrentTime() {
			return this.$refs.videoPlayer?.currentTime ?? this.position;
		},
		setMute(value) {
			if (value && this.volumeLevel === 0) {
				this.setVolume(50);
			}
			this.$refs.videoPlayer.muted = value;
			this.isMuted = value;
		},
		setVolume(value) {
			this.$refs.videoPlayer.volume = value / 100;
			this.volumeLevel = value;
			if (value > 0 && this.isMuted) {
				this.setMute(false);
			} else if (value === 0) {
				this.setMute(true);
			}
		},
		handleLoadedMetadata() {
			this.updateDuration();
			this.getPlayerData();
			this.updateNativeAudioTrackInformation();
			if (requestedPosition) {
				this.$refs.videoPlayer.currentTime = parseFloat(parseFloat(requestedPosition) + this.streamLeadTimeSeconds);
			} else {
				this.$refs.videoPlayer.currentTime = this.tagTime ? this.tagTime.start : (0 + this.streamLeadTimeSeconds);
			}
		},
		handleCropperReady() {
			this.setCropBoxToFullHeight();
			this.syncCropBoxData();
		},
		handleCropStart() {
			this.$emit('cropstart');
		},
		// Cropper keeps its own generated DOM; resize can run after Vue refs are removed.
		isCropperConnected() {
			return !!(
				this.cropper &&
				this.$refs.cropContainer?.isConnected &&
				this.$refs.cropCanvas?.isConnected &&
				this.cropper.container?.isConnected
			);
		},
		// Cropper's resize handler reads private cached dimensions and crashes if they are not initialized yet.
		isCropperReadyForResize() {
			return !!(
				this.isCropperConnected() &&
				this.cropper.containerData?.width > 0 &&
				this.cropper.containerData?.height > 0
			);
		},
		getCropContainerSize() {
			return {
				width:  this.$refs.cropContainer?.clientWidth ?? 0,
				height: this.$refs.cropContainer?.clientHeight ?? 0,
			};
		},
		syncCropBoxData() {
			if (!this.isCropperConnected()) {
				return;
			}

			const cropContainerSize = this.getCropContainerSize();
			const cropBoxData = this.cropper.getCropBoxData();

			if (cropContainerSize.width <= 0 || cropContainerSize.height <= 0 || cropBoxData.width <= 8) {
				return;
			}

			this.$emit('coordinates-update', cropBoxData);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.multi-format-player {
	position: relative;
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;

	&.is-mobile-portrait.is-preview {
		height: 100%;
	}
}

.multi-format-player__media-w {
	display: flex;
	justify-content: center;
	flex: 1 1 auto;
	min-height: 0;
	background-color: var(--color-black);

	.multi-format-player.is-mobile-portrait.is-preview & {
		height: 100%;
	}
}

.multi-format-player__media-w-inner {
	position: relative;
	width: 100%;
	max-width: 100%;
	aspect-ratio: 16 / 9;
	margin: 0 auto;

	@supports (width: min(100%, 1px)) {
		max-width: min(100%, calc(var(--adaptive-max-height, 100vw) * 16 / 9));
	}

	.multi-format-player.is-mobile-portrait.is-preview & {
		height: 100%;
		width: 100%;
		aspect-ratio: 16 / 9;
		max-height: none;
		max-width: none;
	}
}

.multi-format-player__video-container {
	overflow-x: clip;
	overflow-y: visible;
	position: relative;
	width: 100%;
	height: 100%;
	aspect-ratio: 16 / 9;

	.multi-format-player.is-mobile-portrait.is-preview & {
		height: 100%;
		width: 100%;
		aspect-ratio: 16 / 9;
	}

	.multi-format-player.is-ready &,
	.multi-format-player.is-recording & {
		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			pointer-events: none;
			z-index: 1;
			border: 1px solid var(--color-hype-yellow);
		}
	}

	.multi-format-player.is-recording & {
		&::after {
			border-color: var(--color-red);
		}
	}
}

.multi-format-player__video-player {
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	position: absolute;
	object-fit: contain;
}

.multi-format-player__controls {
	&.is-inactive {
		pointer-events: none;
		opacity: 0.4;
	}

	&.is-hidden {
		visibility: hidden;
		pointer-events: none;
		opacity: 0;
	}

	// Keep desktop media height stable when preview hides the playback controls.
	.multi-format-player.is-mobile-portrait.is-preview & {
		display: none;
	}
}

.multi-format-player__crop-container {
	position: absolute;
	top: 0;
	left: 0;
	overflow-x: clip;
	overflow-y: visible;
	width: 100%;
	height: 100%;
}

.multi-format-player__crop-canvas {
	display: block;
	width: 100%;
	height: 100%;
}

::v-deep {
	.cropper-container {
		overflow: hidden;
	}

	.cropper-face {
		background-color: transparent;
	}

	.cropper-wrap-box {
		pointer-events: none;
	}

	.cropper-canvas,
	.cropper-drag-box {
		pointer-events: none;
		cursor: default;
	}

	.cropper-view-box {
		outline: none;
		border: none;
	}

	.cropper-crop-box {
		box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.75); // stylelint-disable-line
		will-change: transform;
	}

	.has-crop-graphic .cropper-crop-box::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		background-image: var(--multi-format-player-crop-graphic);
		background-position: center;
		background-repeat: no-repeat;
		background-size: contain;
	}
}

.multi-format-player.has-active-keyframe {
	::v-deep .cropper-crop-box {
		box-shadow: 0 0 0 2px var(--color-hype-yellow), 0 0 0 9999px rgba(0, 0, 0, 0.75); // stylelint-disable-line
	}
}

</style>
